import typia, { type tags } from "typia";
import type { ColumnSpec } from "../components/settings/FixSettings";
import type { ViewMode } from "../domain";

export interface GridState {
  columnState?: unknown[];
  filterModel?: Record<string, unknown>;
  [key: string]: unknown;
}

export type GridStateMap = Record<string, GridState>;

export interface EmbeddedDictionary {
  filename: string;
  content: string;
}

/** Synchronized timeline projections over the normalized FIX event model. */
export type TimelineView =
  | "message-activity"
  | "sequence-flow"
  | "replay-recovery"
  | "session-health"
  | "order-lifecycle"
  | "latency"
  | "execution-metrics"
  | "swimlane"
  | "density"
  | "anomaly";

/**
 * URL-backed state shared across timeline views. Selection still flows through
 * the existing `selectedRowIndices`; this carries only timeline-specific state.
 * `from`/`to` are epoch ms (numbers, not ISO) to keep the encoded URL small.
 */
export interface TimelineUrlState {
  view: TimelineView;
  session?: string;
  from?: number;
  to?: number;
  msgTypes?: string[];
  filters?: Record<string, string>;
}

export interface Memory {
  fixMessages: string;
  columns: ColumnSpec[];
  /** Schema version for `columns` (see COLUMNS_VERSION). Absent = legacy
   * (custom columns only); on load the default columns are prepended. */
  columnsVersion?: number;
  title?: string;
  dictionary?: EmbeddedDictionary;
  splitPaneLeftPct?: number;
  viewMode?: ViewMode;
  selectedRowIndices?: (number & tags.Type<"uint32">)[];
  gridStates?: GridStateMap;
  excludeDateFromMessageTime?: boolean;
  timeline?: TimelineUrlState;
  /** Code Lens editor content, inlined when small enough. */
  code?: string;
  /** localStorage key holding Code Lens content too large to inline. */
  codeRef?: string;
}

const validator = typia.createValidateEquals<Memory>();

const formatError = (
  errors: readonly { path: string; expected: string }[] | undefined,
): string => {
  if (!errors || errors.length === 0) return "invalid shape";
  const first = errors[0];
  const path = first.path || "(root)";
  return `${path} expected ${first.expected}`;
};

export type ValidationResult =
  | { ok: true; value: Memory }
  | { ok: false; error: string };

export const validateMemory = (value: unknown): ValidationResult => {
  const result = validator(value);
  if (result.success) {
    return { ok: true, value: result.data };
  }
  return { ok: false, error: formatError(result.errors) };
};
